import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AccordionText } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/AccordionText";
import { ChevronIcon } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/ChevronIcon";
import { CustomizedCollapsableComponent } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/CustomizedCollapsableComponent";
import useToggleAccordion from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/useToggleAccordion";
import { getLineHeight, SCROLL_HEIGHT_MARGIN_OF_ERROR } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/utils";
import { Column, Row, HorizontalLine } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import React from "react";
import styled from "styled-components";
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Accordion/Accordion#Accordion"
 */ export var AccordionContainer = function(param) {
    var title = param.title, maxNumberOfLinesShownWhenCollapsed = param.maxNumberOfLinesShownWhenCollapsed, text = param.text, visibleComponent = param.visibleComponent, visibleComponentInSpan = param.visibleComponentInSpan, onExpandAccordionItem = param.onExpandAccordionItem, isListOfItems = param.isListOfItems, collapsedComponent = param.collapsedComponent, expandedComponent = param.expandedComponent, isCustomizedCollapsableComponent = param.isCustomizedCollapsableComponent, onView = param.onView;
    var _accordionContentRef_current, _expandedComponent_props, _collapsedComponent_props;
    var viewRef = useDoActionWhenVisible(onView);
    var _React_useState = _sliced_to_array(React.useState(true), 2), hasExpandableContent = _React_useState[0], setHasExpandableContent = _React_useState[1];
    var _useToggleAccordion = useToggleAccordion({
        hasExpandableContent: hasExpandableContent,
        onExpandAccordionItem: onExpandAccordionItem
    }), isAccordionOpen = _useToggleAccordion.isAccordionOpen, toggleIsAccordionOpen = _useToggleAccordion.toggleIsAccordionOpen;
    var _React_useState1 = _sliced_to_array(React.useState(isAccordionOpen), 2), isFullTextVisible = _React_useState1[0], setIsFullTextVisible = _React_useState1[1];
    var accordionContentRef = React.useRef(null);
    var maxHeightOfTextWhenCollapsed = maxNumberOfLinesShownWhenCollapsed * getLineHeight() * SCROLL_HEIGHT_MARGIN_OF_ERROR;
    var _React_useState2 = _sliced_to_array(React.useState(maxHeightOfTextWhenCollapsed), 2), textExpandedHeight = _React_useState2[0], setTextExpandedHeight = _React_useState2[1];
    React.useLayoutEffect(function() {
        if (!accordionContentRef.current) {
            return;
        }
        // The content height may change (for example when a product with a different description is selected)
        setTextExpandedHeight(accordionContentRef.current.scrollHeight);
        if (accordionContentRef.current.scrollHeight > maxHeightOfTextWhenCollapsed) {
            setHasExpandableContent(true);
        } else if (accordionContentRef.current.scrollHeight <= maxHeightOfTextWhenCollapsed) {
            setHasExpandableContent(false);
        }
    }, [
        maxHeightOfTextWhenCollapsed,
        (_accordionContentRef_current = accordionContentRef.current) === null || _accordionContentRef_current === void 0 ? void 0 : _accordionContentRef_current.scrollHeight
    ]);
    React.useEffect(function() {
        if (isAccordionOpen) {
            setIsFullTextVisible(true);
        }
    }, [
        isAccordionOpen
    ]);
    var handleTransitionEnd = React.useCallback(function() {
        if (!isAccordionOpen) {
            setIsFullTextVisible(false);
        }
    }, [
        isAccordionOpen
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(StyledAccordionContainer, {
                $hasExpandableContent: hasExpandableContent,
                onClick: toggleIsAccordionOpen,
                ref: onView ? viewRef : undefined,
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansMedium",
                                children: title
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "1x"
                            }),
                            isCustomizedCollapsableComponent ? /*#__PURE__*/ _jsx(CustomizedCollapsableComponent, {
                                $isAccordionOpen: isAccordionOpen,
                                $componentExpandedHeight: expandedComponent === null || expandedComponent === void 0 ? void 0 : (_expandedComponent_props = expandedComponent.props) === null || _expandedComponent_props === void 0 ? void 0 : _expandedComponent_props.height,
                                $componentCollapsedHeight: collapsedComponent === null || collapsedComponent === void 0 ? void 0 : (_collapsedComponent_props = collapsedComponent.props) === null || _collapsedComponent_props === void 0 ? void 0 : _collapsedComponent_props.height,
                                children: isAccordionOpen ? expandedComponent : collapsedComponent
                            }) : /*#__PURE__*/ _jsxs(AccordionText, {
                                "data-test-id": "accordion-text",
                                $isAccordionOpen: isAccordionOpen,
                                $isFullTextVisible: isFullTextVisible,
                                onTransitionEnd: handleTransitionEnd,
                                ref: accordionContentRef,
                                $maxNumberOfLinesShownWhenCollapsed: maxNumberOfLinesShownWhenCollapsed,
                                $textExpandedHeight: textExpandedHeight,
                                $textCollapsedHeight: maxHeightOfTextWhenCollapsed,
                                $isListOfItems: isListOfItems,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraphSansRegular",
                                        children: text
                                    }),
                                    visibleComponentInSpan
                                ]
                            }),
                            visibleComponent
                        ]
                    }),
                    hasExpandableContent ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Spacer, {
                                width: "3x"
                            }),
                            /*#__PURE__*/ _jsx(ChevronIcon, {
                                isAccordionOpen: isAccordionOpen
                            })
                        ]
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(HorizontalLine, {})
        ]
    });
};
var StyledAccordionContainer = styled(Row).withConfig({
    componentId: "sc-9d34d0ec-0"
})([
    "width:100%;padding:",
    ";cursor:",
    ";",
    "{padding-top:",
    ";}"
], getSpacing("3x", 0), function(param) {
    var $hasExpandableContent = param.$hasExpandableContent;
    return $hasExpandableContent ? "pointer" : "unset";
}, responsiveMediaQueries.mobile, getSpacing("1x", 0));
